import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './About.scss';


import SEO from '../../components/SEO';

const NeuralCanvas = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animId;
        let W, H;
        const NODES = 80;
        const MAX_DIST = 160;
        const nodes = [];
        const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
        const init = () => {
            nodes.length = 0;
            for (let i = 0; i < NODES; i++) nodes.push({
                x: Math.random() * W, y: Math.random() * H,
                vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
                r: Math.random() * 1.5 + 0.5,
            });
        };
        const draw = () => {
            ctx.clearRect(0, 0, W, H);
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < MAX_DIST) {
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(120,160,255,${(1 - dist / MAX_DIST) * 0.35})`;
                        ctx.lineWidth = 0.6;
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            }
            for (const n of nodes) {
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(140,180,255,0.7)';
                ctx.fill();
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0 || n.x > W) n.vx *= -1;
                if (n.y < 0 || n.y > H) n.vy *= -1;
            }
            animId = requestAnimationFrame(draw);
        };
        resize(); init(); draw();
        window.addEventListener('resize', () => { resize(); init(); });
        return () => cancelAnimationFrame(animId);
    }, []);
    return <canvas ref={canvasRef} className="dg-canvas" />;
};

const About = () => {
    const { t } = useTranslation();
    const [heroReady, setHeroReady] = useState(false);
    const sectionsRef = useRef([]);
    const values = t('about.values', { returnObjects: true });
    const stats = t('about.stats', { returnObjects: true });

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => setHeroReady(true), 200);
        const obs = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('dg-visible'); }),
            { threshold: 0.08 }
        );
        sectionsRef.current.forEach(el => el && obs.observe(el));
        return () => { clearTimeout(timer); obs.disconnect(); };
    }, []);

    const addRef = i => el => sectionsRef.current[i] = el;

    return (
        <div className="dg-page">
            <SEO title="À propos" description="KANEM est une agence digitale basée à Montréal. Sites web, réseaux sociaux et automatisations IA." url="/a-propos" />

            {/* HERO */}
            <section className="dg-hero">
                <NeuralCanvas />
                <div className="dg-hero-vignette" />
                <div className="dg-hero-content">
                    <div className={`dg-badge ${heroReady ? 'dg-badge--in' : ''}`}>
                        <span className="dg-badge-dot" />
                        {t('about.label')}
                    </div>
                    <div className="dg-hero-title">
                        <div className="dg-reveal-line">
                            <span className="dg-reveal-inner" style={{
                                transitionDelay: '0.3s',
                                transform: heroReady ? 'translateY(0)' : 'translateY(110%)',
                                opacity: heroReady ? 1 : 0,
                            }}>
                                {t('about.hero_title')}
                            </span>
                        </div>
                    </div>
                    <div className="dg-hero-sub">
                        {t('about.hero_tagline').split(' ').map((word, i) => (
                            <span key={i} className="dg-word-wrap">
                                <span className="dg-word" style={{
                                    transitionDelay: `${0.5 + i * 0.035}s`,
                                    transform: heroReady ? 'translateY(0)' : 'translateY(120%)',
                                    opacity: heroReady ? 1 : 0,
                                }}>
                                    {word}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
                <div className="dg-scroll-indicator">
                    <div className="dg-scroll-mouse"><div className="dg-scroll-wheel" /></div>
                </div>

            </section>

            {/* STORY */}
            <section className="dg-process dg-reveal" ref={addRef(0)}>
                <div className="dg-inner">
                    <p className="dg-label">{t('about.story_label')}</p>
                    <h2 className="dg-title">{t('about.story_title')}</h2>
                    <div className="ab-story-grid">
                        <p>{t('about.story_p1')}</p>
                        <p>{t('about.story_p2')}</p>
                        <p>{t('about.story_p3')}</p>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="dg-stats dg-reveal" ref={addRef(1)}>
                <div className="dg-inner dg-stats-grid">
                    {Array.isArray(stats) && stats.map((s, i) => (
                        <div className="dg-stat" key={i}>
                            <div className="dg-stat-num">{s.num}</div>
                            <p>{s.label}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* VALUES */}
            <section className="dg-process dg-reveal" ref={addRef(2)}>
                <div className="dg-inner">
                    <p className="dg-label">{t('about.values_label')}</p>
                    <h2 className="dg-title">{t('about.values_title')}</h2>
                    <div className="dg-steps">
                        {Array.isArray(values) && values.map((v, i) => (
                            <div className="dg-step" key={v.num} style={{ transitionDelay: `${i * 0.12}s` }}>
                                <span className="dg-step-num">{v.num}</span>
                                <h3>{v.title}</h3>
                                <p>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="dg-cta dg-reveal" ref={addRef(3)}>
                <NeuralCanvas />
                <div className="dg-cta-vignette" />
                <div className="dg-cta-inner">
                    <p className="dg-label">{t('about.banner_title')}</p>
                    <h2>
                        <span className="dg-reveal-line">
                            <span className="dg-reveal-inner">{t('about.banner_desc')}</span>
                        </span>
                    </h2>
                    <Link to="/contact" className="dg-btn-primary large">
                        {t('about.banner_cta')} <span>→</span>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default About;
